import React, { type ReactNode } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import Icon from 'shared/atoms/Icon';
import IconButton, { type IconButtonProps } from 'shared/atoms/IconButton';
import Input from 'shared/atoms/Input';
import CircularSpinner from 'shared/atoms/CircularSpinner';

import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

import type { InputType } from 'shared/atoms/Input/const';

const SpinnerWrapper = styled.div`
  padding: ${theme.spacing.xs};
`;

interface SearchFieldProps {
  autoFocus?: boolean;
  disabled?: boolean;
  endAdornment?: ReactNode;
  iconActions?: Omit<
    IconButtonProps,
    'ariaLabel' | 'icon' | 'small' | 'sx' | 'sxIcon' | 'theme' | 'type' | 'user'
  >;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  loading?: boolean;
  onChange?: (event: { name: string; value: string }) => void;
  onClear?: () => void;
  placeholder?: string;
  theme?: BackgroundThemes;
  type?: InputType;
  value?: string;
}

const SearchField = ({
  disabled = false,
  iconActions,
  loading = false,
  onChange,
  onClear,
  theme: themeLayout = 'dark',
  type = 'primary',
  value,
  ...props
}: SearchFieldProps) => {
  const { t } = useTranslation();

  const onTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;
    onChange?.({ name, value });
  };

  const getIcon = (
    iconActions?: SearchFieldProps['iconActions'],
    loading?: boolean
  ): ReactNode => {
    if (loading) {
      return (
        <SpinnerWrapper>
          <CircularSpinner color="inherit" size={16} static thickness={5} />
        </SpinnerWrapper>
      );
    }

    return iconActions ? (
      <IconButton
        ariaLabel={t('generic.search')}
        icon={SearchIcon}
        small
        sx={{
          transform: 'rotate(90deg)',
        }}
        theme={type === 'primary' || themeLayout === 'light' ? 'light' : 'dark'}
        type="transparent"
        {...iconActions}
      />
    ) : (
      <Icon
        icon={SearchIcon}
        sx={{
          transform: 'rotate(90deg)',
        }}
      />
    );
  };

  const showClearButton = !disabled && onClear && value;

  return (
    <Input
      disabled={disabled}
      iconLeft={getIcon(iconActions, loading)}
      iconRight={
        showClearButton ? (
          <IconButton
            ariaLabel={t('generic.close')}
            icon={CloseIcon}
            onClick={() => onClear?.()}
            small
            theme={
              type === 'primary' || themeLayout === 'light' ? 'light' : 'dark'
            }
            type="transparent"
          />
        ) : null
      }
      onChange={onTextInputChange}
      theme={themeLayout}
      type={type}
      value={value}
      {...props}
    />
  );
};

export default SearchField;
export type { SearchFieldProps };
